import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, FileText, Users, Home, Upload, Menu, X, Folder, Pencil, Mail, DollarSign, CheckSquare, Link as LinkIcon, ShoppingCart, LogOut, Image } from 'lucide-react';
import { api } from './services/api';
import DashboardView from './components/DashboardView';
import FileHandler from './components/FileHandler';
import GanttView from './components/GanttView';
import TasksView from './components/TasksView';
import TaskModal from './components/TaskModal';
import ProjectManager from './components/ProjectManager';
import EmailSettings from './components/EmailSettings';
import ShoppingListView from './components/ShoppingListView';
import FinancialsView from './components/FinancialsView';
import PunchListView from './components/PunchListView';
import ClientPortal from './components/ClientPortal';
import GalleryView from './components/GalleryView';
import PlansView from './components/PlansView';
import ContractorDirectory from './components/ContractorDirectory';
import Login from './components/Login';
import { ToastProvider, useToast } from './components/Toast';

console.log("Construction Manager v37.1 (Plans Fixed)");

const AppContent = () => {
  if(window.location.pathname.startsWith('/client/')) return <ClientPortal />;
  
  const { showToast } = useToast();
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [activeView, setActiveView] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [projectData, setProjectData] = useState({ 
      projectName: 'Loading...', projectStartDate: new Date().toISOString().split('T')[0], 
      tasks: [], communications: [], files: [], emailNotifications: [], emailSchedule: [], emailTime: '08:00',
      changeOrders: [], punchList: [], shoppingList: [], roomSpecs: [], clientToken: '', contractors: [],
      workingCapital: 50000, interestRate: 7.5
  });
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const projManagerRef = useRef();

  const handleLogin = (t) => { localStorage.setItem('authToken', t); setToken(t); };
  const handleLogout = () => { localStorage.removeItem('authToken'); setToken(null); };

  const loadProjectsList = useCallback(async () => {
      try { const list = await api.getProjects(); setProjectsList(list); return list; } 
      catch (e) { if(e.message === 'Unauthorized') handleLogout(); return []; }
  }, []);
  
  const loadProject = useCallback(async (id) => {
    if(!token) return; setLoading(true);
    try {
      let targetId = id;
      if (!targetId) {
        const list = await loadProjectsList();
        if (list.length > 0) targetId = list[0].id;
        else { setCurrentProjectId(null); setProjectData({ projectName: 'New Project', projectStartDate: new Date().toISOString().split('T')[0], tasks: [], communications: [], files: [], emailNotifications: [], emailSchedule: [], emailTime: '08:00', changeOrders:[], punchList:[], shoppingList: [], roomSpecs: [], contractors: [], workingCapital: 50000, interestRate: 7.5 }); setLoading(false); return; }
      }
      const data = await api.getProject(targetId);
      setCurrentProjectId(targetId);
      setProjectData({ 
          ...data, tasks: data.tasks||[], communications: data.communications||[], files: data.files||[], 
          emailNotifications: data.emailNotifications||[], emailSchedule: data.emailSchedule||[], emailTime: data.emailTime||'08:00',
          changeOrders: data.changeOrders||[], punchList: data.punchList||[], shoppingList: data.shoppingList||[],
          roomSpecs: data.roomSpecs||[], contractors: data.contractors||[]
      });
    } catch (err) { if(err.message === 'Unauthorized') handleLogout(); } finally { setLoading(false); }
  }, [loadProjectsList, token]);

  useEffect(() => { if(token) { loadProject(); loadProjectsList(); } }, [loadProject, loadProjectsList, token]);

  const handleUpdateProject = async (updates) => {
      if (!currentProjectId) return;
      const newData = { ...projectData, ...updates }; setProjectData(newData);
      try { await api.syncProject(currentProjectId, newData); } catch(e) { showToast('Save Failed', 'error'); }
  };

  const handleSave = async (overrideData) => {
      setLoading(true);
      const data = overrideData || projectData;
      try { 
          if (currentProjectId) { await api.syncProject(currentProjectId, data); showToast('Saved'); } 
          else { const res = await api.createProject(data); if (res.id) { setCurrentProjectId(res.id); showToast('Created!'); await loadProjectsList(); } }
      } catch(e) { showToast('Error', 'error'); } finally { setLoading(false); }
  };

  const handleCreateProject = async (data) => {
      setCurrentProjectId(null);
      const res = await api.createProject({ ...data, emailNotifications: data.emailNotifications || [] });
      if (res.id) { setCurrentProjectId(res.id); await loadProjectsList(); loadProject(res.id); showToast('Project Created!'); }
  };

  const handleDeleteProject = async (id) => {
      if(window.confirm('Delete this project?')) { await api.deleteProject(id); showToast('Project Deleted'); loadProject(null); }
  };

  const handleAddTask = async (taskData) => {
      if (!currentProjectId) return showToast("Save Project first!", "error");
      const isNew = !taskData.id; if(isNew) taskData.id = Date.now();
      const newTasks = isNew ? [...projectData.tasks, taskData] : projectData.tasks.map(t => t.id === taskData.id ? taskData : t);
      setProjectData({...projectData, tasks: newTasks}); setShowTaskModal(false);
      try {
          if(isNew) { const sTask = await api.createTask(currentProjectId, taskData); setProjectData(prev => ({ ...prev, tasks: prev.tasks.map(t => t.id === taskData.id ? { ...t, ...sTask } : t) })); } 
          else { await api.updateTask(taskData.id, taskData, currentProjectId); }
          showToast('Task Saved'); if(taskData.startDate || taskData.duration) setTimeout(() => loadProject(currentProjectId), 500);
      } catch(e) { showToast('Save Error', 'error'); }
  };

  const handleDeleteTask = async (taskId) => {
      setProjectData(prev => ({ ...prev, tasks: prev.tasks.filter(t => t.id !== taskId) })); setShowTaskModal(false); 
      try { await api.deleteTask(taskId); showToast('Task Deleted'); } catch(e) { showToast('Delete Failed', 'error'); }
  };

  const handleFileUpload = async (file, taskId = null, category = 'General') => {
      if (!currentProjectId) return showToast("Save project first", "error");
      try {
          const result = await api.uploadFileAtomic(file, currentProjectId, taskId, category);
          const newFile = { ...result, id: result.id, uploadDate: result.uploadDate || new Date().toISOString().split('T')[0] };
          setProjectData(p => ({...p, files: [...p.files, newFile]})); showToast('Uploaded');
      } catch (err) { showToast('Upload Failed', 'error'); }
  };

  const deleteFile = async (fid) => { if(window.confirm('Delete?')) { try { await api.deleteProjectFile(fid); setProjectData(p => ({...p, files: p.files.filter(f => f.id !== fid)})); showToast('Deleted'); } catch(e) { showToast('Error', 'error'); } } };
  
  if (!token) return <Login onLogin={handleLogin} />;

  const renderContent = () => {
    switch(activeView) {
      case 'dashboard': return <DashboardView tasks={projectData.tasks} communications={projectData.communications} changeOrders={projectData.changeOrders} punchList={projectData.punchList} shoppingList={projectData.shoppingList} onViewChange={(v) => { setActiveView(v); setIsMobileMenuOpen(false); }} onEditTask={(t) => { setEditingTask(t); setShowTaskModal(true); }} />;
      case 'schedule': return <GanttView tasks={projectData.tasks} projectStartDate={projectData.projectStartDate} onEditTask={(t) => { setEditingTask(t); setShowTaskModal(true); }} />;
      case 'financials': return <FinancialsView tasks={projectData.tasks} projectData={projectData} onUpdateProject={handleUpdateProject} />;
      case 'shopping': return <ShoppingListView items={projectData.shoppingList} setItems={i => setProjectData(p=>({...p, shoppingList:i}))} projectId={currentProjectId} />;
      case 'punchlist': return <PunchListView items={projectData.punchList} setItems={i => setProjectData(p=>({...p, punchList:i}))} onUpload={handleFileUpload} projectId={currentProjectId} />;
      case 'gallery': return <GalleryView files={projectData.files} tasks={projectData.tasks} />;
      
      // FIX: Added projectId prop here
      case 'plans': return <PlansView files={projectData.files} setFiles={f => setProjectData(p=>({...p, files:f}))} roomSpecs={projectData.roomSpecs} setRoomSpecs={s => setProjectData(p=>({...p, roomSpecs:s}))} onUpload={handleFileUpload} projectId={currentProjectId} />;
      
      case 'tasks': return <TasksView tasks={projectData.tasks} onAddTask={handleAddTask} onEditTask={(t) => { setEditingTask(t); setShowTaskModal(true); }} />;
      case 'team': return <ContractorDirectory contractors={projectData.contractors} projectId={currentProjectId} onUpdate={() => loadProject(currentProjectId)} />;
      case 'documents': return <FileHandler files={projectData.files} setFiles={f => setProjectData(p=>({...p, files:f}))} projectId={currentProjectId} onUpload={handleFileUpload} onDelete={deleteFile} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
      <nav className="bg-white border-b sticky top-0 z-30 px-4 py-3 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-3"><button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-gray-600"><Menu size={24} /></button><div className="flex flex-col">{isEditingTitle ? (<input autoFocus className="text-xl font-bold text-blue-700 border-b-2 border-blue-500 outline-none" value={projectData.projectName} onChange={(e) => setProjectData({...projectData, projectName: e.target.value})} onBlur={() => { setIsEditingTitle(false); handleSave(); }} onKeyDown={(e) => { if(e.key === 'Enter') { setIsEditingTitle(false); handleSave(); } }} />) : (<div className="flex items-center gap-2 group cursor-pointer" onClick={() => setIsEditingTitle(true)}><h1 className="text-xl font-bold text-blue-700 leading-none">{projectData.projectName}</h1><Pencil size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"/></div>)}<span className="text-xs text-gray-500">Started: {projectData.projectStartDate}</span></div></div>
        <div className="flex items-center gap-2">
            <button onClick={() => setShowEmailModal(true)} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg mr-1"><Mail size={20}/></button>
            <button onClick={() => projManagerRef.current.setIsOpen(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg flex gap-2 items-center"><Folder size={20}/></button>
            <button onClick={handleLogout} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><LogOut size={20}/></button>
            <button onClick={handleSave} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium">Save</button>
        </div>
      </nav>
      <div className="flex max-w-7xl mx-auto relative">
        <div className={`fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`} onClick={() => setIsMobileMenuOpen(false)}></div>
        <aside className={`fixed inset-y-0 left-0 bg-white z-50 w-64 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 transition duration-200 ease-in-out border-r md:block py-6 px-4 min-h-[calc(100vh-64px)] overflow-y-auto shadow-xl md:shadow-none`}>
            <div className="space-y-1">
                {[{ id: 'dashboard', icon: Home, label: 'Overview' }, { id: 'schedule', icon: Calendar, label: 'Gantt Schedule' }, { id: 'plans', icon: FileText, label: 'Plans & Specs' }, { id: 'gallery', icon: Image, label: 'Build Gallery' }, { id: 'financials', icon: DollarSign, label: 'Financials' }, { id: 'shopping', icon: ShoppingCart, label: 'Shopping List' }, { id: 'team', icon: Users, label: 'Team' }, { id: 'punchlist', icon: CheckSquare, label: 'Punch List' }, { id: 'tasks', icon: FileText, label: 'Tasks' }, { id: 'documents', icon: Upload, label: 'Documents' }].map(item => (<button key={item.id} onClick={() => { setActiveView(item.id); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${activeView === item.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50 text-gray-600'}`}><item.icon size={18} />{item.label}</button>))}
            </div>
        </aside>
        <main className="flex-1 p-4 md:p-6 overflow-x-hidden">{renderContent()}</main>
      </div>
      {showTaskModal && <TaskModal task={editingTask} allTasks={projectData.tasks} projectFiles={projectData.files} contractors={projectData.contractors} onClose={() => setShowTaskModal(false)} onSave={handleAddTask} onDelete={handleDeleteTask} onUploadFile={handleFileUpload} onDeleteFile={deleteFile} />}
      {showEmailModal && <EmailSettings project={projectData} onClose={() => setShowEmailModal(false)} onSave={(emails, sched, time) => {
    const updated = { ...projectData, emailNotifications: emails, emailSchedule: sched, emailTime: time };
    setProjectData(updated);
    handleSave(updated);
    setShowEmailModal(false);
}} />}
      <ProjectManager ref={(r) => projManagerRef.current = { setIsOpen: r?.setIsOpen }} currentProject={projectData} projects={projectsList} onLoadProject={loadProject} onProjectCreate={handleCreateProject} onDeleteProject={handleDeleteProject} />
    </div>
  );
};
const App = () => ( <ToastProvider> <AppContent /> </ToastProvider> );
export default App;